import React from "react";
import { motion } from "framer-motion";
import { Flame, Bug, FlaskConical, ExternalLink } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, CartesianGrid,
} from "recharts";
import { openRubber } from "../../lib/track";

const cards = [
  {
    icon: Flame,
    title: "Fire Risk",
    stat: "206 fires",
    statLabel:
      "in Harrisonburg, VA between 2008 and 2014. Sixteen damaged buildings. The city later passed a mulch ordinance in response.",
    body:
      "Wood mulch is a documented ignition source for home fires. A discarded cigarette, an ember from a grill, even reflected sunlight can smolder unnoticed in dry bark for hours before flames erupt. The NFPA reports thousands of landscape mulch fires across the U.S. every year — roughly half traced to discarded smoking materials.",
    source: "CBC News — Harrisonburg Fire Department, mulch fire ordinance",
    sourceUrl: "https://www.cbc.ca/1.5220737",
    testid: "card-fire",
  },
  {
    icon: Bug,
    title: "Pests & Pathogens",
    stat: "Termites. Ants. Fungus.",
    statLabel: "Wood mulch is an invitation, not a barrier.",
    body:
      "Bark and shredded wood feed termites and carpenter ants. \"Artillery fungus\" shoots black spores onto siding and cars. Sour mulch can kill the very plants it's supposed to protect.",
    source: "Penn State Extension — artillery fungus",
    sourceUrl: "https://extension.psu.edu/artillery-fungus-in-mulch",
    testid: "card-pests",
  },
  {
    icon: FlaskConical,
    title: "Chemical Leaching",
    stat: "Arsenic. Chromium.",
    statLabel: "Dyed mulch is often recycled CCA-treated lumber.",
    body:
      "Much of the dyed wood mulch sold at big-box stores is ground-up pressure-treated lumber. Over years, those preservatives leach into the soil — the soil where kids play, pets dig, and tomatoes grow.",
    source: "EPA — chromated arsenicals (CCA)",
    sourceUrl: "https://www.epa.gov/ingredients-used-pesticide-products/chromated-arsenicals-cca",
    testid: "card-chemicals",
  },
];

const fireData = [
  { name: "Wood mulch", value: 4, color: "#A13815" },
  { name: "Rubber mulch", value: 0, color: "#525252" },
];

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0F0F0F] border border-[#262626] px-3 py-2 text-xs">
      <div className="text-[#E8E5DE]">{payload[0].payload.name}</div>
      <div className="text-[#A3A3A3]">ignitions out of 8 cigarette trials: {payload[0].value}</div>
    </div>
  );
};

export default function SafetyProblem() {
  return (
    <section
      data-testid="safety-section"
      className="relative w-full py-28 sm:py-36 px-6 bg-[#0F0F0F] border-t border-[#1A1A1A]"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1.0 }}
          className="mb-16 sm:mb-20 max-w-3xl"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#C4451C] mb-5">
            the case, part one
          </p>
          <h2 className="font-marker text-[#E8E5DE] text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
            Wood mulch isn't just decorative.
            <br />
            <span className="text-[#C4451C]">It's combustible, toxic,</span>
            <br />
            and alive with things you didn't invite.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.article
                key={c.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.9, delay: i * 0.1 }}
                className="rust-hover bg-[#141414] border border-[#262626] p-7 sm:p-8 flex flex-col gap-5"
                data-testid={c.testid}
              >
                <div className="flex items-center gap-3">
                  <Icon size={22} className="text-[#C4451C]" strokeWidth={1.5} />
                  <span className="text-xs tracking-[0.25em] uppercase text-[#A3A3A3]">
                    {c.title}
                  </span>
                </div>
                <div>
                  <div className="font-marker text-3xl sm:text-4xl text-[#E8E5DE] leading-tight">
                    {c.stat}
                  </div>
                  <div className="text-sm text-[#A3A3A3] mt-2">{c.statLabel}</div>
                </div>
                <p className="text-[#E8E5DE]/80 text-[15px] leading-relaxed">{c.body}</p>
                <a
                  href={c.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${c.testid}-source`}
                  className="mt-auto text-[11px] tracking-wider uppercase text-[#525252] hover:text-[#C4451C] inline-flex items-center gap-1.5 transition-colors"
                >
                  Source: {c.source}
                  <ExternalLink size={11} />
                </a>
              </motion.article>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1.0 }}
          className="bg-[#141414] border border-[#262626] p-6 sm:p-10"
          data-testid="fire-chart-wrapper"
        >
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#C4451C] mb-2">
                cigarette ignition test
              </p>
              <h3 className="font-marker text-2xl sm:text-3xl text-[#E8E5DE]">
                When a cigarette lands on your mulch.
              </h3>
              <p className="text-sm text-[#A3A3A3] mt-2 max-w-xl">
                The NFPA reports that roughly half of all U.S. mulch fires are caused by
                improperly discarded smoking materials. In controlled 8-trial cigarette
                ignition testing by researchers at The Ohio State University, recycled-pallet
                wood mulch — the most common decorative mulch sold at major retailers —
                ignited four times. Rubber mulch never ignited at all.
              </p>
            </div>
            <button
              onClick={() => openRubber("safety-shop-link", "safety", "https://recycledrubber.com/")}
              data-testid="safety-shop-link"
              className="text-xs tracking-[0.25em] uppercase text-[#C4451C] hover:text-[#E8E5DE] flex items-center gap-2 self-start sm:self-end"
            >
              See rubber mulch options <ExternalLink size={14} />
            </button>
          </div>

          <div className="h-72 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={fireData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="#262626" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#A3A3A3", fontSize: 12 }} axisLine={{ stroke: "#262626" }} tickLine={false} />
                <YAxis
                  domain={[0, 8]}
                  ticks={[0, 2, 4, 6, 8]}
                  tick={{ fill: "#737373", fontSize: 11 }}
                  axisLine={{ stroke: "#262626" }}
                  tickLine={false}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(196,69,28,0.06)" }} />
                <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                  {fireData.map((d, idx) => (
                    <Cell key={idx} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-[#525252] mt-4 tracking-wider leading-relaxed">
            Cigarette ignition data from{" "}
            <a
              href="https://joa.isa-arbor.com/articles.asp?JournalID=1&VolumeID=29&IssueID=6"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#C4451C]"
            >
              <em>Steward, Sydnor &amp; Bishop (2003), The Ease of Ignition of 13 Landscape Mulches, Journal of Arboriculture 29(6): 317–321</em>
            </a>
            , as cited in the{" "}
            <a
              href="https://www.cbc.ca/1.5220737"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#C4451C]"
            >
              <em>CBC News coverage of the Harrisonburg mulch ordinance</em>
            </a>
            . Wood mulch value reflects ground-recycled-pallet results — the predominant
            decorative mulch sold at U.S. home improvement retailers. NFPA cigarette-source
            statistic:{" "}
            <a
              href="https://www.nfpa.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#C4451C]"
            >
              <em>nfpa.org</em>
            </a>
            .
          </p>
        </motion.div>
      </div>
    </section>
  );
}
